/* ============================================
   ActivityLog — System Activity Log
   Centralized log viewer for all ERP activity.
   Filterable by tags, tipo, and date range.
   ============================================ */
import { useState, useEffect, useMemo } from 'react'
import { useSupabase } from '@/hooks/useSupabase'
import { useCachedData } from '@/hooks/useCachedData'
import { showToast } from '@/components/layout/Toast'
import {
  IconActivity, IconRefresh, IconFilter, IconCheck, IconXCircle,
  IconAlertTriangle, IconAlertCircle, IconChevronDown, IconSearch,
  IconX, IconExternalLink,
} from '@/components/icons/Icons'
import type { SystemLog } from '@/lib/types'
import { PageHeader } from '@/components/ui'

/* ─── Constants ─── */

const ALL_TAGS = [
  'crm', 'cotizaciones', 'facturacion', 'sunat', 'cobros',
  'proyectos', 'contabilidad', 'whatsapp', 'email', 'chat',
  'notificaciones', 'storage', 'google', 'auth', 'config',
  'media', 'api', 'error',
] as const

const TAG_COLORS: Record<string, string> = {
  whatsapp: '#25D366',
  email: '#4F7FE8',
  chat: '#8B5CF6',
  facturacion: '#F59E0B',
  sunat: '#EF4444',
  cobros: '#10B981',
  crm: '#3B82F6',
  proyectos: '#6366F1',
  contabilidad: '#14B8A6',
  storage: '#6B7280',
  media: '#EC4899',
  api: '#F97316',
  error: '#EF4444',
  notificaciones: '#A855F7',
  google: '#4285F4',
  auth: '#64748B',
  config: '#78716C',
}

const TIPO_ICONS: Record<string, React.ReactNode> = {
  success: <IconCheck size={14} />,
  error: <IconXCircle size={14} />,
  warning: <IconAlertTriangle size={14} />,
  info: <IconAlertCircle size={14} />,
}

const TIPO_LABELS: Record<string, string> = {
  success: 'OK',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
}

const PAGE_SIZE = 50

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = now - then
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'ahora'
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('es-PE', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

export default function ActivityLog() {
  const db = useSupabase()

  // Filters
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedTipo, setSelectedTipo] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showTagPicker, setShowTagPicker] = useState(false)

  // Expanded rows
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  /* El log entero se trae UNA vez y se guarda; filtrar y paginar pasa a ser
     cuenta en memoria. Antes cada cambio de filtro volvia a bajar la tabla
     completa de `system_log` para filtrarla igual en el navegador: el filtro
     tardaba lo mismo que la primera carga. */
  const { data: todos = [], isLoading: loading, refrescar } = useCachedData(
    ['system_log'],
    () => db.getAll<SystemLog>('system_log').catch(() => {
      showToast('Error al cargar activity log', 'error')
      return [] as SystemLog[]
    }),
    { staleTime: 60 * 1000 },
  )

  const filtrados = useMemo(() => {
    let filtered = todos
    // Por etiquetas (todas tienen que estar)
    if (selectedTags.length > 0) {
      filtered = filtered.filter(log => selectedTags.every(tag => log.tags.includes(tag)))
    }
    if (selectedTipo !== 'all') {
      filtered = filtered.filter(log => log.tipo === selectedTipo)
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      filtered = filtered.filter(log =>
        log.descripcion.toLowerCase().includes(q) ||
        log.accion.toLowerCase().includes(q) ||
        (log.error && log.error.toLowerCase().includes(q))
      )
    }
    if (dateFrom) filtered = filtered.filter(log => log.created_at >= dateFrom)
    if (dateTo) {
      const toEnd = dateTo + 'T23:59:59'
      filtered = filtered.filter(log => log.created_at <= toEnd)
    }
    return [...filtered].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  }, [todos, selectedTags, selectedTipo, busqueda, dateFrom, dateTo])

  // "Cargar mas" ya no pide nada: sube el corte de la lista que ya esta aca.
  const [visibles, setVisibles] = useState(PAGE_SIZE)
  useEffect(() => { setVisibles(PAGE_SIZE) }, [selectedTags, selectedTipo, busqueda, dateFrom, dateTo])
  const logs = useMemo<SystemLog[]>(() => filtrados.slice(0, visibles), [filtrados, visibles])
  const hasMore = filtrados.length > logs.length
  const loadingMore = false
  const loadLogs = refrescar

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    )
  }

  const removeTag = (tag: string) => {
    setSelectedTags(prev => prev.filter(t => t !== tag))
  }

  const clearFilters = () => {
    setSelectedTags([])
    setSelectedTipo('all')
    setSearchQuery('')
    setDateFrom('')
    setDateTo('')
  }

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // El boton (y Enter) aplican el texto escrito; el filtrado es local, asi que
  // no hay nada que pedir.
  const handleSearch = () => { setBusqueda(searchQuery.trim()) }

  /* ─── Stats ─── */
  const totalErrors = logs.filter(l => l.tipo === 'error').length
  const hasActiveFilters = selectedTags.length > 0 || selectedTipo !== 'all' || searchQuery || dateFrom || dateTo

  return (
    <div className="page-enter actlog-page">
      <PageHeader
        breadcrumbs={[{ label: 'Activity Log' }]}
        actions={
          <button className="btn btn-secondary" onClick={() => void loadLogs()} disabled={loading}>
            <IconRefresh size={16} /> Actualizar
          </button>
        }
      />

      {/* Filters Bar */}
      <div className="actlog-filters">
        {/* Tag filters */}
        <div className="actlog-filters__tags">
          {selectedTags.length > 0 && (
            <div className="actlog-active-tags">
              {selectedTags.map(tag => (
                <button
                  key={tag}
                  className="actlog-tag-chip actlog-tag-chip--active"
                  style={{ '--tag-color': TAG_COLORS[tag] || '#6B7280' } as React.CSSProperties}
                  onClick={() => removeTag(tag)}
                  type="button"
                >
                  {tag} <IconX size={10} />
                </button>
              ))}
            </div>
          )}
          <div className="actlog-tag-picker-wrapper">
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setShowTagPicker(!showTagPicker)}
              type="button"
            >
              <IconFilter size={14} /> Filtrar por modulo
              <IconChevronDown size={12} className={showTagPicker ? 'actlog-chevron--open' : ''} />
            </button>
            {showTagPicker && (
              <div className="actlog-tag-picker">
                {ALL_TAGS.map(tag => (
                  <button
                    key={tag}
                    className={`actlog-tag-option ${selectedTags.includes(tag) ? 'actlog-tag-option--selected' : ''}`}
                    style={{ '--tag-color': TAG_COLORS[tag] || '#6B7280' } as React.CSSProperties}
                    onClick={() => toggleTag(tag)}
                    type="button"
                  >
                    <span className="actlog-tag-dot" />
                    {tag}
                    {selectedTags.includes(tag) && <IconCheck size={12} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Type filter */}
        <div className="actlog-filters__tipo">
          {['all', 'error', 'warning', 'success', 'info'].map(t => (
            <button
              key={t}
              className={`actlog-tipo-btn ${selectedTipo === t ? 'actlog-tipo-btn--active' : ''} ${t !== 'all' ? `actlog-tipo-btn--${t}` : ''}`}
              onClick={() => setSelectedTipo(t)}
              type="button"
            >
              {t === 'all' ? 'Todos' : TIPO_LABELS[t]}
              {t === 'error' && totalErrors > 0 && (
                <span className="actlog-tipo-badge">{totalErrors}</span>
              )}
            </button>
          ))}
        </div>

        {/* Search + Date */}
        <div className="actlog-filters__search">
          <div className="actlog-search-input">
            <IconSearch size={14} />
            <input
              type="text"
              placeholder="Buscar en logs..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <input
            type="date"
            className="actlog-date-input"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            title="Desde"
          />
          <input
            type="date"
            className="actlog-date-input"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            title="Hasta"
          />
          {hasActiveFilters && (
            <button className="btn btn-sm btn-secondary" onClick={clearFilters} type="button">
              <IconX size={12} /> Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Log entries */}
      {loading ? (
        <div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 60 }}>
          <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
          Cargando activity log...
        </div>
      ) : logs.length === 0 ? (
        <div className="actlog-empty">
          <IconActivity size={48} />
          <h3>{hasActiveFilters ? 'Sin resultados' : 'Sin actividad registrada'}</h3>
          <p>{hasActiveFilters ? 'Prueba ajustando los filtros' : 'La actividad del sistema aparecera aqui'}</p>
          {hasActiveFilters && (
            <button className="btn btn-secondary" onClick={clearFilters} type="button">
              Limpiar filtros
            </button>
          )}
        </div>
      ) : (
        <div className="actlog-list">
          {logs.map(log => {
            const isExpanded = expandedIds.has(log.id)
            const hasDetails = log.metadata && Object.keys(log.metadata).length > 0
            const hasError = !!log.error

            return (
              <div
                key={log.id}
                className={`actlog-entry actlog-entry--${log.tipo} ${isExpanded ? 'actlog-entry--expanded' : ''}`}
                onClick={() => (hasDetails || hasError) && toggleExpand(log.id)}
                role={hasDetails || hasError ? 'button' : undefined}
                tabIndex={hasDetails || hasError ? 0 : undefined}
              >
                <div className="actlog-entry__main">
                  <div className="actlog-entry__icon">
                    {TIPO_ICONS[log.tipo]}
                  </div>
                  <div className="actlog-entry__content">
                    <div className="actlog-entry__desc">{log.descripcion}</div>
                    <div className="actlog-entry__tags">
                      {log.tags.map(tag => (
                        <span
                          key={tag}
                          className="actlog-tag-chip"
                          style={{ '--tag-color': TAG_COLORS[tag] || '#6B7280' } as React.CSSProperties}
                          onClick={e => { e.stopPropagation(); toggleTag(tag) }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="actlog-entry__meta">
                    <span className="actlog-entry__time" title={formatDateTime(log.created_at)}>
                      {timeAgo(log.created_at)}
                    </span>
                    {(hasDetails || hasError) && (
                      <IconChevronDown
                        size={12}
                        className={`actlog-entry__chevron ${isExpanded ? 'actlog-entry__chevron--open' : ''}`}
                      />
                    )}
                  </div>
                </div>

                {/* Error display */}
                {log.error && (
                  <div className="actlog-entry__error">
                    {log.error.slice(0, 200)}{log.error.length > 200 ? '...' : ''}
                  </div>
                )}

                {/* Expanded details */}
                {isExpanded && hasDetails && (
                  <div className="actlog-entry__details">
                    <div className="actlog-entry__detail-header">
                      <span>Metadata</span>
                      {log.entidad_tipo && log.entidad_id && (
                        <span className="actlog-entry__entity">
                          <IconExternalLink size={12} />
                          {log.entidad_tipo} #{log.entidad_id}
                        </span>
                      )}
                    </div>
                    <pre className="actlog-entry__json">
                      {JSON.stringify(log.metadata, null, 2)}
                    </pre>
                    {log.error && (
                      <>
                        <div className="actlog-entry__detail-header" style={{ marginTop: 8 }}>
                          Error completo
                        </div>
                        <pre className="actlog-entry__json actlog-entry__json--error">
                          {log.error}
                        </pre>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* Load more */}
          {hasMore && (
            <button
              className="actlog-load-more"
              onClick={() => setVisibles(v => v + PAGE_SIZE)}
              disabled={loadingMore}
              type="button"
            >
              {loadingMore ? 'Cargando...' : 'Cargar mas...'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
